import React, { useState } from 'react';
import { formatCurrency } from '../../utils/currencyFormatter';
import { ArrowUpIcon, ArrowDownIcon, CheckCircleIcon, ClockIcon, AlertCircleIcon, DollarSignIcon, CalendarIcon, BuildingIcon, UserIcon, FilterIcon, DownloadIcon } from 'lucide-react';
export function InvoicePaymentStatusReport({
  filters,
  locale = 'en-US'
}) {
  const [sortField, setSortField] = useState('dueDate');
  const [sortDirection, setSortDirection] = useState('asc');
  // Sample data for the report
  const invoices = [{
    id: 'INV-2023-001',
    invoiceNumber: 'INV-45678',
    vendor: 'Office Depot',
    vendorId: 'vendor-001',
    amount: 1250.75,
    currency: 'USD',
    issueDate: '2023-10-05',
    dueDate: '2023-11-04',
    paymentDate: '2023-10-30',
    status: 'Paid',
    paymentMethod: 'Bank Transfer',
    paymentReference: 'REF-BT-001',
    entity: 'Global Technologies Inc.',
    entityId: 'ent-001',
    department: 'IT Department',
    approvedBy: 'John Smith',
    notes: 'Office supplies for Q4'
  }, {
    id: 'INV-2023-002',
    invoiceNumber: 'INV-98765',
    vendor: 'Dell Technologies',
    vendorId: 'vendor-002',
    amount: 5430.0,
    currency: 'USD',
    issueDate: '2023-10-10',
    dueDate: '2023-11-09',
    paymentDate: null,
    status: 'Pending',
    paymentMethod: null,
    paymentReference: null,
    entity: 'Global Technologies Inc.',
    entityId: 'ent-001',
    department: 'IT Department',
    approvedBy: 'Jane Doe',
    notes: 'New laptops for engineering team'
  }, {
    id: 'INV-2023-003',
    invoiceNumber: 'INV-12345',
    vendor: 'PrintWorks Inc.',
    vendorId: 'vendor-003',
    amount: 3200.5,
    currency: 'USD',
    issueDate: '2023-09-15',
    dueDate: '2023-10-15',
    paymentDate: null,
    status: 'Overdue',
    paymentMethod: null,
    paymentReference: null,
    entity: 'European Operations Ltd',
    entityId: 'ent-002',
    department: 'Marketing',
    approvedBy: 'Alice Johnson',
    notes: 'Marketing materials for trade show'
  }, {
    id: 'INV-2023-004',
    invoiceNumber: 'INV-67890',
    vendor: 'Server Solutions Inc.',
    vendorId: 'vendor-004',
    amount: 12500.0,
    currency: 'USD',
    issueDate: '2023-10-20',
    dueDate: '2023-11-19',
    paymentDate: null,
    status: 'Pending',
    paymentMethod: null,
    paymentReference: null,
    entity: 'Global Technologies Inc.',
    entityId: 'ent-001',
    department: 'IT Department',
    approvedBy: 'John Smith',
    notes: 'Server hardware upgrade'
  }, {
    id: 'INV-2023-005',
    invoiceNumber: 'INV-54321',
    vendor: 'Office Depot',
    vendorId: 'vendor-001',
    amount: 750.25,
    currency: 'USD',
    issueDate: '2023-09-01',
    dueDate: '2023-10-01',
    paymentDate: '2023-09-25',
    status: 'Paid',
    paymentMethod: 'Credit Card',
    paymentReference: 'REF-CC-001',
    entity: 'Asia Pacific Division',
    entityId: 'ent-003',
    department: 'Operations',
    approvedBy: 'Bob Wilson',
    notes: 'Office supplies for Singapore office'
  }, {
    id: 'INV-2023-006',
    invoiceNumber: 'INV-13579',
    vendor: 'Tech Solutions Inc.',
    vendorId: 'vendor-005',
    amount: 8750.0,
    currency: 'EUR',
    issueDate: '2023-09-10',
    dueDate: '2023-10-10',
    paymentDate: null,
    status: 'Overdue',
    paymentMethod: null,
    paymentReference: null,
    entity: 'European Operations Ltd',
    entityId: 'ent-002',
    department: 'IT Department',
    approvedBy: 'Claire Martin',
    notes: 'IT consulting services'
  }, {
    id: 'INV-2023-007',
    invoiceNumber: 'INV-24680',
    vendor: 'Global Shipping Co.',
    vendorId: 'vendor-006',
    amount: 1875.5,
    currency: 'USD',
    issueDate: '2023-10-25',
    dueDate: '2023-11-24',
    paymentDate: null,
    status: 'Pending',
    paymentMethod: null,
    paymentReference: null,
    entity: 'Global Technologies Inc.',
    entityId: 'ent-001',
    department: 'Logistics',
    approvedBy: 'David Brown',
    notes: 'Shipping fees for Q4 product deliveries'
  }, {
    id: 'INV-2023-008',
    invoiceNumber: 'INV-97531',
    vendor: 'Cleaning Services Ltd',
    vendorId: 'vendor-007',
    amount: 950.0,
    currency: 'GBP',
    issueDate: '2023-10-01',
    dueDate: '2023-10-31',
    paymentDate: '2023-10-28',
    status: 'Paid',
    paymentMethod: 'Bank Transfer',
    paymentReference: 'REF-BT-002',
    entity: 'European Operations Ltd',
    entityId: 'ent-002',
    department: 'Facilities',
    approvedBy: 'Emma Scott',
    notes: 'Monthly cleaning services for London office'
  }];
  // Apply filters based on the filters prop
  const filteredInvoices = invoices.filter(invoice => {
    // Filter by entity if specified
    if (filters.entity && invoice.entityId !== filters.entity) {
      return false;
    }
    // Filter by vendor if specified
    if (filters.vendor && invoice.vendorId !== filters.vendor) {
      return false;
    }
    // Filter by status if specified
    if (filters.status) {
      const statusLower = filters.status.toLowerCase();
      const invoiceStatusLower = invoice.status.toLowerCase();
      if (statusLower === 'pending-approval' && invoiceStatusLower !== 'pending') {
        return false;
      } else if (statusLower !== 'pending-approval' && statusLower !== invoiceStatusLower) {
        return false;
      }
    }
    // Filter by date range
    if (filters.startDate && filters.endDate) {
      const invoiceDate = new Date(invoice.issueDate);
      const startDate = new Date(filters.startDate);
      const endDate = new Date(filters.endDate);
      if (invoiceDate < startDate || invoiceDate > endDate) {
        return false;
      }
    }
    return true;
  });
  // Sort the filtered invoices
  const sortedInvoices = [...filteredInvoices].sort((a, b) => {
    if (sortField === 'amount') {
      return sortDirection === 'asc' ? a.amount - b.amount : b.amount - a.amount;
    } else if (sortField === 'dueDate') {
      const dateA = new Date(a.dueDate);
      const dateB = new Date(b.dueDate);
      return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
    } else if (sortField === 'issueDate') {
      const dateA = new Date(a.issueDate);
      const dateB = new Date(b.issueDate);
      return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
    } else if (sortField === 'vendor') {
      return sortDirection === 'asc' ? a.vendor.localeCompare(b.vendor) : b.vendor.localeCompare(a.vendor);
    } else if (sortField === 'status') {
      return sortDirection === 'asc' ? a.status.localeCompare(b.status) : b.status.localeCompare(a.status);
    }
    return 0;
  });
  // Calculate summary statistics
  const totalAmount = filteredInvoices.reduce((sum, invoice) => sum + invoice.amount, 0);
  const paidInvoices = filteredInvoices.filter(invoice => invoice.status === 'Paid');
  const paidAmount = paidInvoices.reduce((sum, invoice) => sum + invoice.amount, 0);
  const pendingInvoices = filteredInvoices.filter(invoice => invoice.status === 'Pending');
  const pendingAmount = pendingInvoices.reduce((sum, invoice) => sum + invoice.amount, 0);
  const overdueInvoices = filteredInvoices.filter(invoice => invoice.status === 'Overdue');
  const overdueAmount = overdueInvoices.reduce((sum, invoice) => sum + invoice.amount, 0);
  const handleSort = field => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  // Function to render the sort arrow
  const renderSortArrow = field => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ArrowUpIcon className="h-4 w-4" data-id="element-15500" /> : <ArrowDownIcon className="h-4 w-4" data-id="element-15501" />;
  };
  // Function to render the status badge
  const renderStatusBadge = status => {
    switch (status) {
      case 'Paid':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-15502">
            <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-15503" />
            Paid
          </span>;
      case 'Pending':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-15504">
            <ClockIcon className="h-3 w-3 mr-1" data-id="element-15505" />
            Pending
          </span>;
      case 'Overdue':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-15506">
            <AlertCircleIcon className="h-3 w-3 mr-1" data-id="element-15507" />
            Overdue
          </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-15508">
            {status}
          </span>;
    }
  };
  return <div className="p-6" data-id="element-15509">
      {/* Summary cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-6" data-id="element-15510">
        <div className="bg-white overflow-hidden shadow-sm rounded-lg" data-id="element-15511">
          <div className="p-5" data-id="element-15512">
            <div className="flex items-center" data-id="element-15513">
              <div className="flex-shrink-0" data-id="element-15514">
                <DollarSignIcon className="h-6 w-6 text-gray-400" data-id="element-15515" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-15516">
                <dl data-id="element-15517">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-15518">
                    Total Invoices
                  </dt>
                  <dd data-id="element-15519">
                    <div className="text-lg font-medium text-gray-900" data-id="element-15520">
                      {formatCurrency(totalAmount, 'USD', locale)} (
                      {filteredInvoices.length})
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow-sm rounded-lg" data-id="element-15521">
          <div className="p-5" data-id="element-15522">
            <div className="flex items-center" data-id="element-15523">
              <div className="flex-shrink-0" data-id="element-15524">
                <CheckCircleIcon className="h-6 w-6 text-green-500" data-id="element-15525" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-15526">
                <dl data-id="element-15527">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-15528">
                    Paid Invoices
                  </dt>
                  <dd data-id="element-15529">
                    <div className="text-lg font-medium text-gray-900" data-id="element-15530">
                      {formatCurrency(paidAmount, 'USD', locale)} (
                      {paidInvoices.length})
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow-sm rounded-lg" data-id="element-15531">
          <div className="p-5" data-id="element-15532">
            <div className="flex items-center" data-id="element-15533">
              <div className="flex-shrink-0" data-id="element-15534">
                <ClockIcon className="h-6 w-6 text-blue-500" data-id="element-15535" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-15536">
                <dl data-id="element-15537">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-15538">
                    Pending Invoices
                  </dt>
                  <dd data-id="element-15539">
                    <div className="text-lg font-medium text-gray-900" data-id="element-15540">
                      {formatCurrency(pendingAmount, 'USD', locale)} (
                      {pendingInvoices.length})
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow-sm rounded-lg" data-id="element-15541">
          <div className="p-5" data-id="element-15542">
            <div className="flex items-center" data-id="element-15543">
              <div className="flex-shrink-0" data-id="element-15544">
                <AlertCircleIcon className="h-6 w-6 text-red-500" data-id="element-15545" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-15546">
                <dl data-id="element-15547">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-15548">
                    Overdue Invoices
                  </dt>
                  <dd data-id="element-15549">
                    <div className="text-lg font-medium text-gray-900" data-id="element-15550">
                      {formatCurrency(overdueAmount, 'USD', locale)} (
                      {overdueInvoices.length})
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Invoices table */}
      <div className="bg-white shadow-sm rounded-lg overflow-hidden" data-id="element-15551">
        <div className="overflow-x-auto" data-id="element-15552">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-15553">
            <thead className="bg-gray-50" data-id="element-15554">
              <tr data-id="element-15555">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('vendor')} data-id="element-15556">
                  <div className="flex items-center" data-id="element-15557">
                    <span data-id="element-15558">Vendor</span>
                    {renderSortArrow('vendor')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('invoiceNumber')} data-id="element-15559">
                  <div className="flex items-center" data-id="element-15560">
                    <span data-id="element-15561">Invoice #</span>
                    {renderSortArrow('invoiceNumber')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('amount')} data-id="element-15562">
                  <div className="flex items-center" data-id="element-15563">
                    <span data-id="element-15564">Amount</span>
                    {renderSortArrow('amount')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('issueDate')} data-id="element-15565">
                  <div className="flex items-center" data-id="element-15566">
                    <span data-id="element-15567">Issue Date</span>
                    {renderSortArrow('issueDate')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('dueDate')} data-id="element-15568">
                  <div className="flex items-center" data-id="element-15569">
                    <span data-id="element-15570">Due Date</span>
                    {renderSortArrow('dueDate')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('status')} data-id="element-15571">
                  <div className="flex items-center" data-id="element-15572">
                    <span data-id="element-15573">Status</span>
                    {renderSortArrow('status')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15574">
                  Entity
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-15575">
                  Payment Details
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-15576">
              {sortedInvoices.map(invoice => <tr key={invoice.id} className="hover:bg-gray-50" data-id="element-15577">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-15578">
                    {invoice.vendor}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-15579">
                    {invoice.invoiceNumber}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-15580">
                    {formatCurrency(invoice.amount, invoice.currency, locale)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-15581">
                    {new Date(invoice.issueDate).toLocaleDateString(locale)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-15582">
                    {new Date(invoice.dueDate).toLocaleDateString(locale)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-15583">
                    {renderStatusBadge(invoice.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-15584">
                    {invoice.entity}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-15585">
                    {invoice.status === 'Paid' ? <div data-id="element-15586">
                        <div data-id="element-15587">{invoice.paymentMethod}</div>
                        <div className="text-xs text-gray-400" data-id="element-15588">
                          {invoice.paymentReference}
                        </div>
                        <div className="text-xs text-gray-400" data-id="element-15589">
                          Paid on{' '}
                          {new Date(invoice.paymentDate).toLocaleDateString(locale)}
                        </div>
                      </div> : <span className="text-xs italic text-gray-400" data-id="element-15590">
                        {invoice.status === 'Overdue' ? `Overdue by ${Math.floor((new Date() - new Date(invoice.dueDate)) / (1000 * 60 * 60 * 24))} days` : 'Not yet paid'}
                      </span>}
                  </td>
                </tr>)}
              {sortedInvoices.length === 0 && <tr data-id="element-15591">
                  <td colSpan={8} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-15592">
                    No invoices found matching the current filters
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>
      {/* Legend */}
      <div className="mt-6 bg-white shadow-sm rounded-lg p-4" data-id="element-15593">
        <h3 className="text-sm font-medium text-gray-900 mb-2" data-id="element-15594">
          Status Legend
        </h3>
        <div className="flex flex-wrap gap-4" data-id="element-15595">
          <div className="flex items-center" data-id="element-15596">
            <span className="inline-block h-3 w-3 rounded-full bg-green-500 mr-2" data-id="element-15597"></span>
            <span className="text-sm text-gray-600" data-id="element-15598">
              Paid: Invoice has been paid in full
            </span>
          </div>
          <div className="flex items-center" data-id="element-15599">
            <span className="inline-block h-3 w-3 rounded-full bg-blue-500 mr-2" data-id="element-15600"></span>
            <span className="text-sm text-gray-600" data-id="element-15601">
              Pending: Invoice awaiting payment
            </span>
          </div>
          <div className="flex items-center" data-id="element-15602">
            <span className="inline-block h-3 w-3 rounded-full bg-red-500 mr-2" data-id="element-15603"></span>
            <span className="text-sm text-gray-600" data-id="element-15604">
              Overdue: Payment deadline has passed
            </span>
          </div>
        </div>
      </div>
    </div>;
}